import {IconButton, openDialog} from 'platform/components';
import {Box, Center, Heading, Show, Text} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import i18n from '@dms/i18n';
import {SkeletonLoadingBox} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {RECORD_AUDIT_PICKER_DIALOG_SIZE} from '../../constants/RECORD_AUDIT_PICKER_DIALOG_SIZE';
import {RecordAuditBranchPickerDialog} from '../RecordAuditBranchPickerDialog/RecordAuditBranchPickerDialog';

interface RecordAuditBranchSnippetTitleProps extends TestIdProps {
  /** Nil when no branch is assigned/resolved — renders the muted "Unassigned" empty state. */
  branchName: string | Nullish;
  isEditable: boolean;
  isLoading?: boolean;
  onSelect: (branchId: string) => void;
}

/**
 * Branch snippet title: the branch name with an inline edit affordance that opens the
 * branch picker datagrid dialog (mirrors the auditor / paired-record pickers). The
 * picked branch persists immediately via `onSelect`.
 */
export function RecordAuditBranchSnippetTitle(props: RecordAuditBranchSnippetTitleProps) {
  const openBranchPickerDialog = () =>
    openDialog(<RecordAuditBranchPickerDialog onConfirm={props.onSelect} />, {
      title: i18n.t('page.recordAudit.create.fields.branch'),
      size: RECORD_AUDIT_PICKER_DIALOG_SIZE,
    });

  return (
    <>
      {match({isLoading: props.isLoading ?? false, branchName: props.branchName})
        .with({isLoading: true}, () => <SkeletonLoadingBox isLoading height={5} width={40} />)
        // Mirrors SnippetPlaceholder (muted, centered) — the shared empty-state look of
        // header snippets — while keeping the edit affordance available.
        .with({branchName: Pattern.nullish}, () => (
          <Center width="100%" height="100%">
            <Text
              size="small"
              color="tertiary"
              data-testid={suffixTestId('branchName-unassigned', props)}
            >
              {i18n.t('general.labels.unassigned')}
            </Text>
          </Center>
        ))
        .otherwise(({branchName}) => (
          <Heading size={5} headingLevel={6} data-testid={suffixTestId('branchName', props)}>
            {branchName}
          </Heading>
        ))}
      {/* Pinned to the snippet box's top-right corner (the box is position:relative) so the edit
          affordance lands in the same place as the paired-record snippet's, independent of the
          vertically-centered content height. */}
      <Show when={props.isEditable}>
        <Box position="absolute" top={2} right={2}>
          <IconButton
            icon="image/edit"
            size="small"
            onClick={openBranchPickerDialog}
            aria-label={i18n.t('general.actions.edit')}
            data-testid={suffixTestId('branchEdit', props)}
          />
        </Box>
      </Show>
    </>
  );
}
